// src/hooks/useProjectTeam.ts
//
// Two separate questions that used to be one, and shouldn't have been:
//
//   useTeams()        — which teams am I in, and what does each have? A read. Any surface that needs
//                       to name a team or check its plan uses this and nothing else.
//   useProjectTeam()  — which team will the NEXT project I create belong to, and therefore whose
//                       credits will pay for it? A choice, made in one place: the picker above Start
//                       QA at creation time.
//
// Settings keeps its own account selection. Inspecting an account never changes the payer
// selected here for the next project.
//
// The choice is a module singleton rather than Redux state because it has to be readable OUTSIDE
// React — buildCreatePayload in OnboardingChat is a plain function — and `useProjectTeam` subscribes
// components to the same store so the picker stays in sync wherever it renders.

import { useSyncExternalStore } from 'react'
import { useGetUsageQuery } from '../services/projectDataApi'
import type { TeamCreditSummary } from '../data/accountTypes'

const STORAGE_KEY = 'loopqa.new-project-team'

let chosenTeamId: string | null = readStored()
const listeners = new Set<() => void>()

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // Private-mode / blocked storage: the choice just doesn't survive a reload.
    return null
  }
}

/** The team explicitly chosen for the next project, or null to take the default (your own team). */
export function getNewProjectTeamId(): string | null {
  return chosenTeamId
}

export function setNewProjectTeamId(teamId: string | null): void {
  if (chosenTeamId === teamId) return
  chosenTeamId = teamId
  try {
    if (teamId) window.localStorage.setItem(STORAGE_KEY, teamId)
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Non-fatal — the in-memory value still drives this session.
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Every team the user belongs to, with the balance and plan of each.
 *
 * Sourced from the un-parameterized `/api/usage` request that every other consumer shares, so asking
 * this question costs no extra round trip. `skip` mirrors RTK Query's: pass it for a surface that also
 * renders to anonymous visitors, where the authenticated request would only ever 401.
 */
export function useTeams(options?: { skip?: boolean }): { teams: TeamCreditSummary[]; isLoading: boolean; isError: boolean; refetch: () => void } {
  const usageQuery = useGetUsageQuery(undefined, { skip: options?.skip })
  return {
    teams: usageQuery.data?.teams ?? [],
    isLoading: usageQuery.isLoading && !usageQuery.data,
    isError: usageQuery.isError,
    refetch: () => { void usageQuery.refetch() },
  }
}

/** The user's own team — the one their subscription funds. */
export function ownTeam(teams: TeamCreditSummary[]): TeamCreditSummary | null {
  return teams.find((team) => team.is_owner) ?? null
}

export interface ProjectTeamState {
  teams: TeamCreditSummary[]
  /** The team the next project would be created in. */
  team: TeamCreditSummary | null
  setTeam: (teamId: string | null) => void
  isLoading: boolean
}

/**
 * Which team a new project goes into.
 *
 * Defaults to the user's OWN team: a project you create is yours unless you say otherwise, even when
 * a team you were invited to has more credits. Picking the richer team automatically would quietly
 * hand your work — and its QA results — to someone else's account.
 *
 * A stale choice (a team you left, or an id stored under another account) falls back to that default
 * rather than sending a team_id the server would reject.
 */
export function useProjectTeam(options?: { skip?: boolean }): ProjectTeamState {
  const chosenId = useSyncExternalStore(subscribe, getNewProjectTeamId, () => null)
  const { teams, isLoading } = useTeams(options)
  const chosen = chosenId ? teams.find((team) => team.id === chosenId) ?? null : null

  return { teams, team: chosen ?? ownTeam(teams), setTeam: setNewProjectTeamId, isLoading }
}
